// Monitoring API Service
// Uses the existing authenticated apiClient for all requests
import { axiosInstance } from './apiClient';

/**
 * Monitoring API client
 * All requests use the authenticated apiClient which handles:
 * - IndexedDB token storage with encryption
 * - Automatic token refresh
 * - Session expiry handling
 */
export const monitoringApi = {
  /**
   * Get fleet status overview
   * @returns {Promise} Fleet status data with stats, devices, and services
   */
  getFleetStatus: async () => {
    try {
      const response = await axiosInstance.get('/monitoring/fleet');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch fleet status:', error);
      return {
        success: false,
        error: error.message,
        data: {
          stats: { online: 0, offline: 0, warning: 0, unknown: 0, total: 0 },
          devices: [],
          services: {},
          alerts: { critical: 0, error: 0, warning: 0 }
        }
      };
    }
  },

  /**
   * Get device status by device ID
   * @param {string} deviceId - Device ID or UUID
   * @returns {Promise} Device status data
   */
  getDeviceStatus: async (deviceId) => {
    try {
      const response = await axiosInstance.get(`/monitoring/devices/${deviceId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch device status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get device history
   * @param {string} deviceId - Device ID or UUID
   * @param {number} hours - Number of hours of history to retrieve (default: 24)
   * @returns {Promise} Device history data
   */
  getDeviceHistory: async (deviceId, hours = 24) => {
    try {
      const response = await axiosInstance.get(`/monitoring/devices/${deviceId}/history`, {
        params: { hours }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch device history:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get active alerts
   * @param {Object} params - Optional query parameters
   * @param {string} params.severity - Filter by severity (warning, error, critical)
   * @param {number} params.limit - Maximum number of alerts to return
   * @param {boolean} params.includeResolved - Include resolved alerts
   * @returns {Promise} Active alerts data
   */
  getActiveAlerts: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/monitoring/alerts', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch active alerts:', error);
      return {
        success: false,
        error: error.message,
        data: { alerts: [], count: 0 }
      };
    }
  },

  /**
   * Acknowledge an alert
   * @param {number} alertId - Alert ID to acknowledge
   * @returns {Promise} Acknowledgment result
   */
  acknowledgeAlert: async (alertId) => {
    try {
      const response = await axiosInstance.post(`/monitoring/alerts/${alertId}/acknowledge`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Resolve an alert
   * @param {number} alertId - Alert ID to resolve
   * @returns {Promise} Resolution result
   */
  resolveAlert: async (alertId) => {
    try {
      const response = await axiosInstance.post(`/monitoring/alerts/${alertId}/resolve`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get monitoring events
   * @param {Object} params - Query parameters
   * @param {string} params.deviceId - Filter by device ID
   * @param {number} params.locationId - Filter by location ID
   * @param {string} params.severity - Filter by severity
   * @param {string} params.eventType - Filter by event type
   * @param {number} params.hours - Hours of history (default: 24)
   * @param {number} params.limit - Maximum events to return
   * @returns {Promise} Monitoring events
   */
  getEvents: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/monitoring/events', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch events:', error);
      return {
        success: false,
        error: error.message,
        data: { events: [], count: 0 }
      };
    }
  },

  /**
   * Acknowledge an event
   * @param {number} eventId - Event ID to acknowledge
   * @returns {Promise} Acknowledgment result
   */
  acknowledgeEvent: async (eventId) => {
    try {
      const response = await axiosInstance.post(`/monitoring/events/${eventId}/acknowledge`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to acknowledge event:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get monitoring service status
   * @returns {Promise} Monitoring service status
   */
  getMonitoringStatus: async () => {
    try {
      const response = await axiosInstance.get('/monitoring/status');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch monitoring status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get service status summary
   * @returns {Promise} Service status data
   */
  getServiceStatus: async () => {
    try {
      const response = await axiosInstance.get('/monitoring/services');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch service status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Request immediate status update from a device
   * @param {string} deviceId - Device ID or UUID
   * @returns {Promise} Request result
   */
  requestDeviceStatus: async (deviceId) => {
    try {
      const response = await axiosInstance.post(`/monitoring/devices/${deviceId}/ping`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to request device status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get uptime statistics
   * @param {Object} params - Query parameters
   * @param {number} params.days - Number of days to include (default: 30)
   * @param {number} params.locationId - Optional location filter
   * @returns {Promise} Uptime statistics
   */
  getUptimeStats: async (params = { days: 30 }) => {
    try {
      const response = await axiosInstance.get('/monitoring/uptime', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch uptime stats:', error);
      return {
        success: false,
        error: error.message,
        data: {
          overallUptime: 0,
          dailyUptime: [],
          deviceUptime: []
        }
      };
    }
  },

  /**
   * Get system health overview (for status page)
   * @returns {Promise} System health data
   */
  getSystemHealth: async () => {
    try {
      const response = await axiosInstance.get('/monitoring/health');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      return {
        success: false,
        error: error.message,
        data: {
          services: {},
          stats: {},
          lastUpdated: null
        }
      };
    }
  },

  /**
   * Get downtime incidents
   * @param {Object} params - Query parameters
   * @param {number} params.days - Number of days to include (default: 30)
   * @param {string} params.severity - Filter by severity
   * @returns {Promise} Downtime incidents
   */
  getDowntimeIncidents: async (params = { days: 30 }) => {
    try {
      const response = await axiosInstance.get('/monitoring/incidents', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      return {
        success: false,
        error: error.message,
        data: { incidents: [], total: 0 }
      };
    }
  },

  /**
   * Get status from the external monitoring service
   * @returns {Promise} External monitoring status
   */
  getMonitoringServiceStatus: async () => {
    try {
      // Call the monitoring service directly (monitoring.caro.care)
      const response = await fetch('https://monitoring.caro.care/api/status');
      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to fetch monitoring service status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get deduplicated alerts from monitoring service
   * Returns unique active alerts (one per device) and optional history
   * @param {Object} params - Query parameters
   * @param {boolean} params.includeResolved - Include resolved alerts in history
   * @param {number} params.days - Days of history to include (default: 7)
   * @param {number} params.limit - Maximum alerts to return
   * @returns {Promise} Deduplicated alerts with summary
   */
  getDeduplicatedAlerts: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.includeResolved) queryParams.append('includeResolved', 'true');
      if (params.days) queryParams.append('days', params.days.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const url = `https://monitoring.caro.care/api/alerts${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to fetch deduplicated alerts:', error);
      return {
        success: false,
        error: error.message,
        data: {
          summary: { totalActive: 0, uniqueDevicesAffected: 0, resolvedInPeriod: 0 },
          activeAlerts: [],
          recentHistory: [],
          duplicatesPerDevice: {}
        }
      };
    }
  },

  /**
   * Clean up duplicate alerts in the monitoring service
   * Keeps the oldest active alert per device and resolves newer duplicates
   * @returns {Promise} Cleanup result
   */
  cleanupDuplicateAlerts: async () => {
    try {
      const response = await fetch('https://monitoring.caro.care/api/alerts/cleanup-duplicates', {
        method: 'POST'
      });
      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Failed to cleanup duplicate alerts:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Mark an alert as fixed (set isActive = 0)
   * @param {number} alertId - Alert ID to mark as fixed
   * @returns {Promise} Result
   */
  markAlertAsFixed: async (alertId) => {
    try {
      const response = await axiosInstance.patch(`/monitoring/alerts/${alertId}`, {
        isActive: false,
        resolvedAt: new Date().toISOString()
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to mark alert as fixed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get recent device activity across all devices
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Maximum number of activities (default: 50)
   * @param {number} params.hours - Hours of history (default: 24)
   * @returns {Promise} Recent device activity
   */
  getRecentDeviceActivity: async (params = { limit: 50, hours: 24 }) => {
    try {
      const response = await axiosInstance.get('/monitoring/activity', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch recent device activity:', error);
      return {
        success: false,
        error: error.message,
        data: { activities: [], total: 0 }
      };
    }
  },

  /**
   * Get system-wide activity statistics for graphs
   * @param {Object} params - Query parameters
   * @param {number} params.hours - Hours of history (default: 24)
   * @returns {Promise} Activity statistics
   */
  getActivityStatistics: async (params = { hours: 24 }) => {
    try {
      const response = await axiosInstance.get('/monitoring/activity/stats', { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch activity statistics:', error);
      return {
        success: false,
        error: error.message,
        data: { hourlyStats: [], summary: {} }
      };
    }
  },

  /**
   * Get uptime details for a specific day
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Object} params - Query parameters
   * @param {number} params.offset - Pagination offset (default: 0)
   * @param {number} params.limit - Number of events to fetch (default: 100)
   * @returns {Promise} Detailed uptime information for that day
   */
  getUptimeDetails: async (date, params = {}) => {
    try {
      const response = await axiosInstance.get(`/monitoring/uptime/${date}`, { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch uptime details:', error);
      return {
        success: false,
        error: error.message,
        data: { events: [], stats: {}, pagination: {} }
      };
    }
  },

  /**
   * Get devices affected by a specific service issue
   * @param {string} serviceName - Name of the service
   * @returns {Promise} List of affected devices
   */
  getAffectedDevices: async (serviceName) => {
    try {
      const response = await axiosInstance.get(`/monitoring/services/${serviceName}/devices`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch affected devices:', error);
      return {
        success: false,
        error: error.message,
        data: { devices: [] }
      };
    }
  }
};

export default monitoringApi;
